import { Flex, Box, Text, Code, Heading, Spinner } from "@chakra-ui/react"
import { Brain, Check } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import type { ChatRole, AssistantActionStatus } from "@zrt-ai-office/types/course"
import type { Components } from "react-markdown"

interface GameChatMessageProps {
  role: ChatRole
  content: string
  actionStatus?: AssistantActionStatus
  actionLabel?: string
  isTyping?: boolean
}

/* ── Markdown components for assistant messages ── */
const chatMarkdownComponents: Components = {
  p: ({ children }) => (
    <Text mb="2" lineHeight="1.7" fontSize="sm">
      {children}
    </Text>
  ),
  h1: ({ children }) => (
    <Heading as="h4" size="md" mb="2" mt="3">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h4" size="md" mb="2" mt="3">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h5" size="sm" mb="1" mt="2" fontWeight="semibold">
      {children}
    </Heading>
  ),
  ul: ({ children }) => (
    <Box as="ul" pl="4" mb="2" listStyleType="disc" fontSize="sm">
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box as="ol" pl="4" mb="2" listStyleType="decimal" fontSize="sm">
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box as="li" mb="0.5" lineHeight="1.7">
      {children}
    </Box>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes("language-")
    if (isBlock) {
      return (
        <Box
          as="pre"
          bg="gray.900"
          color="green.300"
          p="3"
          borderRadius="lg"
          overflowX="auto"
          mb="2"
          fontSize="xs"
          lineHeight="1.5"
        >
          <Code bg="transparent" color="inherit" fontSize="xs" whiteSpace="pre">
            {children}
          </Code>
        </Box>
      )
    }
    return (
      <Code bg="gray.100" color="gray.800" px="1" py="0.5" borderRadius="sm" fontSize="xs">
        {children}
      </Code>
    )
  },
  pre: ({ children }) => <>{children}</>,
  strong: ({ children }) => (
    <Text as="strong" fontWeight="bold">
      {children}
    </Text>
  ),
  table: ({ children }) => (
    <Box overflowX="auto" mb="2">
      <Box as="table" w="full" fontSize="xs" borderWidth="1px" borderColor="gray.200" borderRadius="md">
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }) => (
    <Box as="thead" bg="gray.50">
      {children}
    </Box>
  ),
  th: ({ children }) => (
    <Box as="th" px="2" py="1" textAlign="left" fontWeight="semibold" borderBottomWidth="1px" borderColor="gray.200">
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box as="td" px="2" py="1" borderBottomWidth="1px" borderColor="gray.100">
      {children}
    </Box>
  ),
  hr: () => <Box as="hr" my="3" borderColor="gray.300" />,
  blockquote: ({ children }) => (
    <Box
      as="blockquote"
      borderLeftWidth="3px"
      borderLeftColor="gray.400"
      pl="3"
      py="1"
      my="2"
      color="gray.600"
      fontSize="sm"
    >
      {children}
    </Box>
  ),
}

/* ── Typing indicator (three bouncing dots) ── */
function TypingDots() {
  return (
    <Flex gap="1" align="center" py="2" px="1">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="2"
          h="2"
          borderRadius="full"
          bg="gray.400"
          css={{
            animation: `typingBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
            "@keyframes typingBounce": {
              "0%, 60%, 100%": { transform: "translateY(0)" },
              "30%": { transform: "translateY(-4px)" },
            },
          }}
        />
      ))}
    </Flex>
  )
}

/* ── Action status badge ── */
function ActionStatusBadge({
  status,
  label,
}: {
  status: AssistantActionStatus
  label?: string
}) {
  return (
    <Flex align="center" gap="1.5" mb="1.5" fontSize="xs" color="gray.500">
      {status === "thinking" && (
        <>
          <Box
            css={{
              animation: "spin 1.5s linear infinite",
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            <Brain size={14} />
          </Box>
          <Text>{label || "思考中..."}</Text>
        </>
      )}
      {status === "executing" && (
        <>
          <Spinner size="xs" color="blue.400" />
          <Text>{label || "执行中..."}</Text>
        </>
      )}
      {status === "done" && (
        <>
          <Box color="green.500">
            <Check size={14} />
          </Box>
          <Text color="green.600">{label || "完成"}</Text>
        </>
      )}
      {status === "error" && (
        <Text color="red.500">{label || "出错了"}</Text>
      )}
    </Flex>
  )
}

export function GameChatMessage({
  role,
  content,
  actionStatus,
  actionLabel,
  isTyping = false,
}: GameChatMessageProps) {
  /* ── Judge message: centered card ── */
  if (role === "judge") {
    return (
      <Flex justify="center" my="3">
        <Box
          maxW="md"
          w="full"
          borderWidth="2px"
          borderColor="amber.300"
          bg="amber.50"
          borderRadius="xl"
          p="4"
        >
          <Flex align="center" gap="2" mb="2">
            <Text fontSize="lg">⚖️</Text>
            <Text fontWeight="bold" fontSize="sm" color="amber.800">
              裁判判定
            </Text>
          </Flex>
          <Box color="amber.900">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={chatMarkdownComponents}
            >
              {content}
            </ReactMarkdown>
          </Box>
        </Box>
      </Flex>
    )
  }

  /* ── User message: right aligned ── */
  if (role === "user") {
    return (
      <Flex justify="flex-end" my="2">
        <Box
          maxW="75%"
          bg="blue.500"
          color="white"
          px="4"
          py="2.5"
          borderRadius="2xl"
          borderBottomRightRadius="sm"
          fontSize="sm"
          lineHeight="1.7"
        >
          <Text>{content}</Text>
        </Box>
      </Flex>
    )
  }

  /* ── Assistant message: left aligned ── */
  return (
    <Flex gap="2" my="2" align="flex-start">
      {/* Avatar */}
      <Flex
        shrink={0}
        w="8"
        h="8"
        borderRadius="full"
        bg="gray.100"
        align="center"
        justify="center"
        fontSize="md"
        mt="0.5"
      >
        🦞
      </Flex>

      <Box maxW="80%">
        {/* Name */}
        <Text fontSize="xs" color="gray.500" mb="1" fontWeight="medium">
          小龙虾
        </Text>

        {/* Action status */}
        {actionStatus && (
          <ActionStatusBadge status={actionStatus} label={actionLabel} />
        )}

        {/* Message bubble */}
        <Box
          bg="gray.100"
          px="4"
          py="2.5"
          borderRadius="2xl"
          borderTopLeftRadius="sm"
        >
          {isTyping ? (
            <TypingDots />
          ) : (
            <Box color="gray.800">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={chatMarkdownComponents}
              >
                {content}
              </ReactMarkdown>
            </Box>
          )}
        </Box>
      </Box>
    </Flex>
  )
}
